import { db } from "~/utils/db";

export const useCalendarStore = defineStore("calendar", {
    state: () => ({
        initialized: false,
        fetchId: 0,

        lastModified: "",
        previousLastModified: "",
        filtersShown: false,
        searchShown: false,
        dateInteractionsShown: false,
        dateInteractionsDate: "",
        monthSelectionShown: false,
        todayDate: new Date(),
        todayDateISO: new Date().toLocaleDateString("sv"),
        // curDate: "",
        curDateISO: "",
        // curFromDateISO: "",
        // curToDateISO: "",
        calendarMonthDays: [],
        curMonth: "",
        curMonthNumber: -1,
        scrollSpySections: [],
        currentActive: "",
        selectedDateISO: "",
        activeFilters: [],
        activeType: "alles",
        debouncedSearchTerm: "",
        maxPrice: "",
        minPrice: "",
        priceRangeActive: false,

        // orderData -> moved to default store!
        // currentlyProcessingOrdersFor: null,
        // lastRequestFor: {},
        hiddenOrderIds: {},
        slotEdit: {
            modalOpen: false,
            isoEdit: "",
            changed: false,
            isSending: false,
        },
        providerModal: {
            shown: false,
            orderId: "",
            orderDate: "",
            type: "",
            providedByValue: "",
            externalProviderValue: "",
            externalProviderNumberValue: "",
            changed: false,
            isSending: false,
        },

        offTimes: {},
        offTimesFetching: true,
    }),
    getters: {
        filteredOrders(state) {
            const store = useDefaultStore();

            // filter by tags
            // filter by active type
            // filter by searchword
            // - can be order number, name, company, phone, address

            if (!store.orderData?.[store.curShop.url]) return [];

            const orders = store.orderData[store.curShop.url];

            const filteredPerDay = {};

            for (const dayObj of state.calendarMonthDays) {
                const dayISO = dayObj.toLocaleDateString("sv");
                if (!orders[dayISO]) {
                    filteredPerDay[dayISO] = [];
                    continue;
                }
                filteredPerDay[dayISO] = orders[dayISO];

                // tags
                if (state.activeFilters.length) {
                    filteredPerDay[dayISO] = filteredPerDay[dayISO].filter(
                        (o) => {
                            for (const filter of state.activeFilters) {
                                if (o.fsTags.includes(filter)) return true; // one of the filters matches -> its shown!
                            }
                            return false;
                        },
                    );
                }
                // activeType (angebote / bestellungen)
                if (state.activeType != "alles") {
                    filteredPerDay[dayISO] = filteredPerDay[dayISO].filter(
                        (o) => {
                            if (state.activeType == "angebote") {
                                return (
                                    o.status == "angebot" ||
                                    o.status == "angebotsanfrage"
                                );
                            }
                            if (state.activeType == "bestellungen") {
                                return !(
                                    o.status == "angebot" ||
                                    o.status == "angebotsanfrage"
                                );
                            }
                        },
                    );
                }
                // search word
                if (
                    state.debouncedSearchTerm &&
                    state.debouncedSearchTerm.length >= 3
                ) {
                    let keyword = state.debouncedSearchTerm.toLowerCase();
                    if (keyword.startsWith("#")) {
                        keyword = keyword.slice(1);
                    }

                    filteredPerDay[dayISO] = filteredPerDay[dayISO].filter(
                        (o) => {
                            if (
                                o.company.toLowerCase().includes(keyword) ||
                                o.customerName
                                    .toLowerCase()
                                    .includes(keyword) ||
                                o.phone_delivery
                                    .toLowerCase()
                                    .includes(keyword) ||
                                o.phone.toLowerCase().includes(keyword) ||
                                o.shippingAddress
                                    .toLowerCase()
                                    .includes(keyword) ||
                                o.orderNumber.toLowerCase().includes(keyword)
                            ) {
                                return true;
                            }
                        },
                    );
                }

                // Sort by timeSlot
                filteredPerDay[dayISO].sort((a, b) => {
                    if (!a.timeSlot) return 1; // Put orders without timeSlot at the end
                    if (!b.timeSlot) return -1; // Put orders without timeSlot at the end
                    return a.timeSlot.localeCompare(b.timeSlot);
                });
            }

            return filteredPerDay;
        },
    },
    actions: {
        logSelf() {
            console.log(this.$state);
        },
        async initCalendar() {
            this.setupCalendarDates(new Date(this.curMonth + "-01"), true);
            this.setupOfftimes();

            await this.fetchNewOrders();

            this.initialized = true;
        },

        async fetchNewOrders() {
            const store = useDefaultStore();
            const { orchestrateOrderFetch } = useData();

            await orchestrateOrderFetch({
                getDates: () =>
                    this.calendarMonthDays.map((d) =>
                        d.toLocaleDateString("sv"),
                    ),
                getFetchId: () => this.fetchId,
                setFetchId: (id: number) => (this.fetchId = id),
                setProcessing: (isProcessing: boolean) =>
                    (store.processing = isProcessing),
                setLoading: (isLoading: boolean) => (store.loading = isLoading),
            });
        },

        setupCalendarDates(date, initial = false) {
            // const curDate = new Date(date);
            this.curMonthNumber = date.getMonth();
            this.curMonth =
                date.getFullYear() +
                "-" +
                String(date.getMonth() + 1).padStart(2, "0");
            this.curDateISO = date.toLocaleDateString("sv");
            if (initial) this.selectedDateISO = this.curDateISO;
            this.calendarMonthDays = [];

            date.setDate(1);
            const firstOfMonth = new Date(date);
            const month = date.getMonth();
            while (date.getMonth() === month) {
                this.calendarMonthDays.push(new Date(date));
                date.setDate(date.getDate() + 1);
            }
            date.setDate(date.getDate() - 1);

            while (firstOfMonth.getDay() != 1) {
                firstOfMonth.setDate(firstOfMonth.getDate() - 1);
                this.calendarMonthDays.unshift(new Date(firstOfMonth));
            }

            while (date.getDay() != 0) {
                date.setDate(date.getDate() + 1);
                this.calendarMonthDays.push(new Date(date));
            }
        },

        async setupOfftimes() {
            const store = useDefaultStore();
            this.offTimesFetching = true;

            const data = {
                from: this.calendarMonthDays[0].toLocaleDateString("sv"),
                to: this.calendarMonthDays[
                    this.calendarMonthDays.length - 1
                ].toLocaleDateString("sv"),
            };

            const ajaxurl = `https://${store.curShop.url}/wp-json/refueat/v1/offtimes?${new URLSearchParams(data).toString()}`;

            try {
                const response = await fetch(ajaxurl, {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${store.auth.jwts[store.curShop.url]}`,
                    },
                });

                if (response.ok) {
                    const data = await response.json();
                    this.offTimes[store.curShop.url] = data;
                    this.offTimesFetching = false;
                }
            } catch (error) {
                this.offTimes[store.curShop.url] = [];
                this.offTimesFetching = false;
            }
        },
    },
    persist: {
        pick: [
            // "previousLastModified",
            // "orderData",
            "hiddenOrderIds",
        ],
    },
});
